import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-hot-toast";

const Register = () => {
    const { login } = useAuth();
    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        companyName: "",
        city: "",
        password: ""
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);

        try {
            await api.post("/api/users/register", form);
            toast.success("Account registered successfully!");
            // Auto login after register
            await login({ email: form.email, password: form.password });
            navigate("/customer");
        } catch (err) {
            setError(err?.response?.data?.message || "Registration failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-[#F7F5EF] text-[#263746] font-sans relative overflow-hidden">
            <div className="w-full max-w-md bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-8 shadow-sm relative z-10">
                <div className="text-center mb-6">
                    <Link to="/" className="inline-block mb-3 group">
                        <img
                            src="/logo.png"
                            alt="LUMIGO Projection Solutions"
                            className="h-12 mx-auto object-contain group-hover:scale-105 transition-transform"
                        />
                    </Link>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E3B629]/10 border border-[#E3B629]/30 text-[#263746] text-[10px] font-black tracking-widest uppercase mb-1">
                        <img src="/favicon.png" alt="LUMIGO Aperture" className="w-3.5 h-3.5 object-contain" />
                        <span>Commercial Client Registration</span>
                    </div>
                    <h1 className="text-2xl font-extrabold text-[#263746]">
                        Create Commercial Account
                    </h1>
                    <p className="text-[#687482] mt-1 text-xs">
                        Register to submit project enquiries, receive quotations, and track order progress.
                    </p>
                </div>

                {error && (
                    <div className="mb-4 text-xs bg-rose-50 border border-rose-200 text-rose-700 p-3 rounded-xl text-center font-bold">
                        {error}
                    </div>
                )}

                <form onSubmit={onSubmit} className="space-y-3.5 text-xs">
                    <div>
                        <label className="text-[10px] font-bold text-[#263746] uppercase tracking-wider block mb-1">
                            Full Name *
                        </label>
                        <input
                            required
                            name="name"
                            value={form.name}
                            onChange={onChange}
                            className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] transition"
                            placeholder="e.g. Ramesh Patel"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] font-bold text-[#263746] uppercase tracking-wider block mb-1">
                                Email Address *
                            </label>
                            <input
                                required
                                name="email"
                                type="email"
                                value={form.email}
                                onChange={onChange}
                                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] transition"
                                placeholder="name@company.com"
                            />
                        </div>

                        <div>
                            <label className="text-[10px] font-bold text-[#263746] uppercase tracking-wider block mb-1">
                                Phone / WhatsApp *
                            </label>
                            <input
                                required
                                name="phone"
                                type="tel"
                                value={form.phone}
                                onChange={onChange}
                                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] transition"
                                placeholder="+91 9876543210"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] font-bold text-[#263746] uppercase tracking-wider block mb-1">
                                Company Name
                            </label>
                            <input
                                name="companyName"
                                value={form.companyName}
                                onChange={onChange}
                                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] transition"
                                placeholder="e.g. Apex Industries"
                            />
                        </div>

                        <div>
                            <label className="text-[10px] font-bold text-[#263746] uppercase tracking-wider block mb-1">
                                City / Region
                            </label>
                            <input
                                name="city"
                                value={form.city}
                                onChange={onChange}
                                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] transition"
                                placeholder="e.g. Pune"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-[10px] font-bold text-[#263746] uppercase tracking-wider block mb-1">
                            Password *
                        </label>
                        <input
                            required
                            name="password"
                            type="password"
                            value={form.password}
                            onChange={onChange}
                            className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] transition"
                            placeholder="••••••••••••"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold py-3 rounded-lg text-xs uppercase tracking-wider transition shadow-sm disabled:opacity-50 mt-2"
                    >
                        {submitting ? "Registering Account..." : "Register Company Account"}
                    </button>
                </form>

                <div className="mt-6 pt-6 border-t border-[#E5E7EB] text-center text-xs text-[#687482]">
                    Already registered?{" "}
                    <Link to="/login" className="text-[#E3B629] hover:underline font-bold">
                        Sign In here
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Register;
